import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PriceTier } from '@kouto/types';
import { RadioGroup } from 'components/RadioGroup';
import { currencyFormat, getCurrencyFromCode } from 'utils';
import styled from 'styled-components';
import type { ITax } from 'types/experience.types';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import type { StayPricing } from 'components/Resource/ResourcePriceTierSelector/NonMappedPriceTierSelector';

interface PriceTierRadioSelectorProps {
  priceTiers: PriceTier[];
  currency: string;
  /** What each tier costs for the picked length, with the words for it; the tier's own rate without. */
  stayPricing?: StayPricing | null;
  taxes?: ITax[];
  value?: string;
  onChange: (tier: string) => void;
}

export const PriceTierRadioSelector = ({
  priceTiers,
  currency,
  stayPricing = null,
  taxes,
  value,
  onChange,
}: PriceTierRadioSelectorProps) => {
  const { t } = useTranslation();
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();

  const options = useMemo(
    () =>
      priceTiers.map((p) => {
        const stayPrice = stayPricing?.priceByTierName[p.name];
        const advertise = (amount: number) =>
          taxes?.length ? calculateAdvertisedTierPrice(amount, taxes) : amount;
        const format = currencyFormat(getCurrencyFromCode(currency));
        const priceLabel =
          stayPrice === undefined
            ? format(advertise(p.price))
            : `${format(advertise(stayPrice))} ${
                stayPricing?.label ?? ''
              }`.trim();
        return {
          value: p.id,
          label: (
            <Label>
              <span className="radio-selector-price-tier-name">{p.name}</span>
              <span className="radio-selector-price-tier-price">
                {p.price === 0 ? t('complimentary') : priceLabel}
              </span>
            </Label>
          ),
        };
      }),
    [priceTiers, currency, stayPricing, taxes, calculateAdvertisedTierPrice, t],
  );

  if (!value) {
    return null;
  }

  return (
    <RadioGroup
      ariaLabel={t('priceTierRadioGroup')}
      onChange={onChange}
      options={options}
      selectedValue={value}
    />
  );
};

const Label = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  > * {
    font-size: 14px;
    font-weight: 400;
    line-height: 15px;

    &:not(:first-child) {
      color: var(--way-colors-contrastColorShades-60);
    }

    &:first-child {
      font-size: 14px;
      font-weight: 600;
      line-height: 15px;
    }
  }
`;
